import React, { useEffect, useState } from "react";
import { Table, Input, Button, Row, Col, Typography, message } from "antd";
import axios from '../../util/axiosConfig';
import { EditOutlined, CheckOutlined, CloseOutlined, DeleteOutlined } from '@ant-design/icons';
import '../../styles/reports.css';

const { Text } = Typography;

interface LineItemProps {
  panelID: number;
  rejectionRemark: string | null;
  isEditOption: boolean;
  status: string | null;
}

const LineItemTable: React.FC<LineItemProps> = ({ panelID, rejectionRemark, isEditOption, status }) => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);

  const [lineItems, setLineItems] = useState<any[]>([]);
  const [editingRow, setEditingRow] = useState<number | null>(null);
  const [editingHSN, setEditingHSN] = useState<string>('');
  const [editingDescription, setEditingDescription] = useState<string>('');
  const [editingQuantity, setEditingQuantity] = useState<number>();
  const [editingUom, setEditingUom] = useState<string>();
  const [editingRate, setEditingRate] = useState<number>();
  const [editingPrice, setEditingPrice] = useState<number>();

  const paginationConfig = {
    current: page,
    pageSize: pageSize,
    total: total,
    showTotal: (total: number, range: [number, number]) => `Showing ${range[0]}-${range[1]} of ${total} entries`,
    showSizeChanger: true,
    pageSizeOptions: ["5", "10", "20", "50"],
    locale: { items_per_page: '' },
    onChange: (newPage: number, newPageSize: number) => {
      setPage(newPage);
      setPageSize(newPageSize);
    },
  };

  const fetchPanelData = async () => {
    try {
      const response = await axios.post(`/get-line-items`, {}, {
        params: {
          pageSize: pageSize,
          pageNo: page - 1,
          invoiceId: panelID,
        },
      });

      if (response.data?.error === null) {
        setLineItems(response?.data?.data?.content);
        setTotal(response?.data?.data?.totalElements);
      } else {
        console.error(response.data?.error, 4);
      }
    } catch (error) {
      console.error('Failed to fetch line item data');
    }
  };

  useEffect(() => {
    if (panelID) {
      fetchPanelData();
    }
    // eslint-disable-next-line
  }, [page, pageSize, panelID]);

  const handleEdit = (record: any) => {
    setEditingRow(record.id);
    setEditingHSN(record.hsn);
    setEditingDescription(record.description);
    setEditingQuantity(record.quantity);
    setEditingUom(record.uom);
    setEditingRate(record.rate);
    setEditingPrice(record.price);
  };

  const cancelEdit = () => {
    setEditingRow(null);
    // setEditingHSN('');
    // setEditingDescription('');
  };

  const updateLineItem = async (record: any) => {
    try {
      const response = await axios.post(`/update-line-items`, {
        id: record.id,
        hsn: editingHSN,
        invoiceId: record.invoiceId,
        itemReference: record.itemReference,
        poItemReference: record.poItemReference,
        taxCode: record.taxCode,
        uom: editingUom,
        quantity: editingQuantity,
        description: editingDescription,
        price: editingPrice,
        rate: editingRate,
        grnReference: record.grnReference,
        grnItemReference: record.grnItemReference,
        grnDocumentYear: record.grnDocumentYear,
        disputeError: record.disputeError,
        inputQuantity: record.inputQuantity,
        inputRate: record.inputRate,
        inputAmountPrice: record.inputAmountPrice
      });
      if (response.data?.error === null) {
        const updatedItems = lineItems.map(item =>
          item.id === record.id ? { ...item, hsn: editingHSN, description: editingDescription, quantity: editingQuantity, uom: editingUom, rate: editingRate, price: editingPrice } : item
        );
        setLineItems(updatedItems);
        setEditingRow(null);
      } else {
        console.error(response.data?.error);
        cancelEdit();
      }
    } catch (error) {
      console.error('Failed to update line item');
      cancelEdit();
    }
  };
  const checkDispute = (str: string, key?: number) =>{
    if(key && str === 'Invoice Item, Invoice Quantity and Invoice Amount does not match with the GRN/SES item and GRN/SES amount.'){
        return 4
    }
    if((str === 'success')||(str === 'Success')||(str === 'success.')||(str === 'Success.') || (!str) || (str === null) || (str === '')){
        return 0
    }else if((str === 'Invoice Item, Invoice Quantity and Invoice Amount does not match with the GRN/SES item and GRN/SES amount.') || (str === 'Invoice item and Invoice amount does not match with the GRN/SES item and GRN/SES amount.') || (str === 'Invoice amount does not match with the GRN/SES amount.') || (str === 'Invoice amount is lesser than GRN/SES amount.') || (str === 'Invoice amount is greater than GRN/SES amount.')){
        return 1
    }else{
        return 2
    }
  }

  const handleDelete = async (record: any) => {
    try {
      const response = await axios.delete(`/delete-line-item?lineItemId=${record.id}`, {});
      if (response.data?.error === null) {
        fetchPanelData();
        message.success(response.data?.message);
      } else {
        console.error(response.data?.error);
      }
    } catch (error) {
      console.error('Failed to delete line item');
    }
  };

  const columns: any = [
    {
      title: 'S.NO',
      dataIndex: 'itemReference',
      key: 'itemReference',
      width: 60,
      render: (_: any, record: any) => (
          <span className={checkDispute(record?.disputeError) === 2 ? 'errorField' : ''}>{record.itemReference}</span>
      ),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      className: 'des-column',
      render: (_: any, record: any) => (
        editingRow === record.id ? (
          <Input
            value={editingDescription}
            onChange={(e) => setEditingDescription(e.target.value)}
          />
        ) : (
          <span className={checkDispute(record?.disputeError) === 2 ? 'errorField' : ''}>{record.description}</span>
        )
      ),
    },
    {
      title: 'HSN/SAC',
      dataIndex: 'hsn',
      key: 'hsn',
      render: (_: any, record: any) => (
        editingRow === record.id ? (
          <Input
            value={editingHSN}
            style={{ width: '100px' }}
            type="text"
            onChange={(e) => setEditingHSN(e.target.value)}
          />
        ) : (
          <span className={checkDispute(record?.disputeError) === 2 ? 'errorField' : ''}>{record.hsn}</span>
        )
      ),
    },
    {
      title: 'Dispute Issue',
      dataIndex: 'disputeError',
      key: 'disputeError',
    },
    {
      title: 'Quantity',
      dataIndex: 'quantity',
      key: 'quantity',
      render: (_: any, record: any) => (
        (editingRow === record.id && (record?.handWrittenInvoiceFlag || record?.editable)) ? (
          <Input
            value={editingQuantity}
            style={{ width: '60px' }}
            type="text"
            onChange={(e) => setEditingQuantity(Number(e.target.value))}
          />
        ) : (
          <span className={((checkDispute(record?.disputeError, 1) === 2) || (checkDispute(record?.disputeError, 1)) === 4) ? 'errorField' : ''}>{record.quantity}</span>
        )
      ),
    },
    {
      title: 'UOM',
      dataIndex: 'uom',
      key: 'uom',
      render: (_: any, record: any) => (
        (editingRow === record.id && (record?.handWrittenInvoiceFlag || record?.editable)) ? (
          <Input
            value={editingUom}
            style={{ width: '60px' }}
            type="text"
            onChange={(e) => setEditingUom(e.target.value)}
          />
        ) : (
          <span className={checkDispute(record?.disputeError) === 2 ? 'errorField' : ''}>{record.uom}</span>
        )
     ),
    },
    {
      title: 'Rate',
      dataIndex: 'rate',
      key: 'rate',
      render: (_: any, record: any) => (
        (editingRow === record.id && (record?.handWrittenInvoiceFlag || record?.editable)) ? (
          <Input
            value={editingRate}
            style={{ width: '80px' }}
            type="text"
            onChange={(e) => setEditingRate(Number(e.target.value))}
          />
        ) : (
          <span className={checkDispute(record?.disputeError) === 2 ? 'errorField' : ''}>{record.rate}</span>
        )
      ),
    },
    {
      title: 'Price',
      dataIndex: 'price',
      key: 'price',
      render: (_: any, record: any) => (
        (editingRow === record.id && (record?.handWrittenInvoiceFlag || record?.editable)) ? (
          <Input
            value={editingPrice}
            style={{ width: '80px' }}
            type="number"
            onChange={(e) => setEditingPrice(Number(e.target.value))}
          />
        ) : (
          <span className={checkDispute(record?.disputeError) !== 0 ? 'errorField' : ''}>{record.price}</span>
        )
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: any) => (
        <>
          {editingRow === record.id ? (
            <>
              <Button
                type="link"
                icon={<CloseOutlined style={{ color: 'red' }} />}
                onClick={cancelEdit}
              />
              <Button
                type="link"
                icon={<CheckOutlined style={{ color: 'green' }} />}
                onClick={() => updateLineItem(record)}
              />
            </>
          ) : (
            (!isEditOption && !status?.toLowerCase().startsWith('reject') && !record?.duplicateInvoiceFlag) &&
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
          )}
          { record?.lineItemsDeleteAccess && !status?.toLowerCase().startsWith('reject') &&
            <Button
              type="link"
              icon={<DeleteOutlined />}
              onClick={() => handleDelete(record)}
            />
          }
        </>
      ),
    },
  ];

  return (
    <>
      {rejectionRemark && (
        <>
          <Row>
            <Col span={24}>
              <Text><span style={{ fontWeight: 'bold' }}>Rejection Remarks : </span> {rejectionRemark}</Text>
            </Col>
          </Row>
          <br />
        </>
      )}
      <Table
        id="lineItemTable"
        dataSource={lineItems}
        size={'small'}
        rowKey="id"
        columns={columns}
        pagination={paginationConfig}
        scroll={{ x: 'max-content' }}
      />
    </>
  );
};

export default LineItemTable;
